import {
  IconArrowRight,
  ParagraphSectionLayout,
  ParagraphTitle,
  TableContainer,
  TableRowContainer,
  TableShellTitle,
  TableShellValue,
} from "@frontend/common";

import { getAxisRoleLabel, useAxisRoleQuery } from "@api/axisRole/axisRole";
import EditableTextContainer from "@EditArticleContainer/shared/components/EditableTextContainer/EditableTextContainer";
import { DEALS_PARAGRAPH_CODE } from "@EditArticleContainer/shared/constants/editArticle.constants";
import { useParagraphEditedData } from "@EditArticleContainer/shared/hooks/useParagraph";
import { useUpdateParagraph } from "@EditArticleContainer/shared/hooks/useUpdateParagraph";
import {
  REF_CODE_FINANCIAL_TERMS_MA_CONTINGENT_MILESTONE,
  REF_CODE_FINANCIAL_TERMS_MA_CONTINGENT_PRICE_PER_SHARE,
  REF_CODE_FINANCIAL_TERMS_MA_CONTINGENT_VALUE,
  REF_CODE_FINANCIAL_TERMS_MA_NUM_SHARES,
  REF_CODE_FINANCIAL_TERMS_MA_OWNERSHIP_EXCHANGE,
  REF_CODE_FINANCIAL_TERMS_MA_TERM_DETAILS,
  REF_CODE_FINANCIAL_TERMS_MA_TOTAL_PRICE_PER_SHARE,
  REF_CODE_FINANCIAL_TERMS_MA_TOTAL_VALUE,
  REF_CODE_FINANCIAL_TERMS_MA_UPFRONT_PRICE_PER_SHARE,
  REF_CODE_FINANCIAL_TERMS_MA_UPFRONT_VALUE,
} from "@EditArticleContainer/shared/libs/refCode/constants/refCode.constants";
import { createRefCode } from "@EditArticleContainer/shared/libs/refCode/utils/createRefCode";

import * as S from "./DealsParagraphFinancialTermsMA.style";
import {
  contingentMilestoneLabels,
  contingentMilestoneText,
  contingentPricePerShareLabels,
  contingentPricePerShareText,
  contingentValueLabels,
  contingentValueText,
  financialTermDetailsText,
  numSharesLabels,
  numSharesText,
  ownershipExchangeTermsText,
  totalPricePerShareLabels,
  totalPricePerShareText,
  totalValueLabels,
  totalValueText,
  upfrontPricePerShareLabels,
  upfrontPricePerShareText,
  upfrontValueLabels,
  upfrontValueText,
} from "./utils/getFinancialTermsMAShellValue";

const PARAGRAPH_TITLE = "Financial Terms";
const VALUE_SHELL_PADDING = "0.2rem";
const EDITABLE_TEXT_STYLE = { padding: "0.8rem", borderRadius: "0" };
const TITLE_WIDTH = "12.2rem";
const VALUE_WIDTH = "30rem";
const VALUE_PER_SHARE_WIDTH = "14.4rem";

const DealsParagraphFinancialTermsMA = () => {
  const paragraph = useParagraphEditedData(
    DEALS_PARAGRAPH_CODE.financialTermsMA,
  );
  const [updateParagraph, loadingIndex, updateKeyMap] = useUpdateParagraph({
    paragraphCode: DEALS_PARAGRAPH_CODE.financialTermsMA,
    sectionCode: "TY002",
  });
  const { data: axisRole } = useAxisRoleQuery();

  if (!paragraph) return null;

  // M&A는 Partner B(Buyer) → Partner A(Target Company) 고정. Role 라벨만 API에서 가져온다.
  const buyerRole = getAxisRoleLabel(axisRole, "B");
  const targetRole = getAxisRoleLabel(axisRole, "A");

  const terms = paragraph.text["Merge & Acquisition terms"];
  const contingents = terms["Contingent Payment"];

  // 행 내 모든 값이 비어있으면 표출하지 않는다. (refKey 정합성을 위해 원본 index 유지)
  const showUpfront =
    upfrontValueText(terms).trim() !== "" ||
    upfrontPricePerShareText(terms).trim() !== "";
  const showTotal =
    totalValueText(terms).trim() !== "" ||
    totalPricePerShareText(terms).trim() !== "";
  const showNumShares = numSharesText(terms).trim() !== "";
  const showOwnership = ownershipExchangeTermsText(terms).trim() !== "";
  const showTermDetails = financialTermDetailsText(terms).trim() !== "";
  const visibleContingents = contingents
    .map((contingent, index) => ({ contingent, index }))
    .filter(
      ({ contingent }) =>
        contingentValueText(contingent).trim() !== "" ||
        contingentPricePerShareText(contingent).trim() !== "" ||
        contingentMilestoneText(contingent).trim() !== "",
    );

  // Payment(Value / Value/Share / Milestone) 값이 하나도 없으면 결제 테이블을 표출하지 않는다.
  const hasPaymentValue =
    showUpfront || showTotal || visibleContingents.length > 0;

  return (
    <ParagraphSectionLayout className="sub-section-wrapper">
      <ParagraphTitle title={PARAGRAPH_TITLE} type="section" />
      <S.FlowRow>
        <S.AxisChipsGroup>
          <S.AxisChip>
            Partner B{buyerRole && <S.AxisRole>({buyerRole})</S.AxisRole>}
          </S.AxisChip>
        </S.AxisChipsGroup>
        <S.ArrowIcon>
          <IconArrowRight width={20} height={20} color="#7c899c" />
        </S.ArrowIcon>
        <S.AxisChipsGroup>
          <S.AxisChip>
            Partner A{targetRole && <S.AxisRole>({targetRole})</S.AxisRole>}
          </S.AxisChip>
        </S.AxisChipsGroup>
      </S.FlowRow>
      <S.ParagraphFinancialTermsMAWrapper key={updateKeyMap.get(0)}>
        <S.ParagraphFinancialTermsMAUpdateButton
          disabled={loadingIndex === 0}
          onClick={() => updateParagraph(0)}
        />
        {hasPaymentValue && (
          <TableContainer>
            <TableRowContainer>
              <TableShellTitle width={TITLE_WIDTH}>{null}</TableShellTitle>
              <TableShellTitle width={VALUE_WIDTH}>Value</TableShellTitle>
              <TableShellTitle width={VALUE_PER_SHARE_WIDTH}>
                Value / Share
              </TableShellTitle>
              <TableShellTitle>Milestone</TableShellTitle>
            </TableRowContainer>

            {showUpfront && (
              <TableRowContainer>
                <TableShellTitle width={TITLE_WIDTH}>Upfront</TableShellTitle>
                <TableShellValue
                  padding={VALUE_SHELL_PADDING}
                  width={VALUE_WIDTH}
                >
                  <EditableTextContainer
                    item={{
                      text: upfrontValueText(terms),
                      labels: upfrontValueLabels(terms),
                    }}
                    refKey={REF_CODE_FINANCIAL_TERMS_MA_UPFRONT_VALUE}
                    canTextEdit
                    style={EDITABLE_TEXT_STYLE}
                  />
                </TableShellValue>
                <TableShellValue
                  padding={VALUE_SHELL_PADDING}
                  width={VALUE_PER_SHARE_WIDTH}
                >
                  <EditableTextContainer
                    item={{
                      text: upfrontPricePerShareText(terms),
                      labels: upfrontPricePerShareLabels(terms),
                    }}
                    refKey={REF_CODE_FINANCIAL_TERMS_MA_UPFRONT_PRICE_PER_SHARE}
                    canTextEdit
                    style={EDITABLE_TEXT_STYLE}
                  />
                </TableShellValue>
                <TableShellValue
                  padding={VALUE_SHELL_PADDING}
                  isEditable={false}
                >
                  {null}
                </TableShellValue>
              </TableRowContainer>
            )}

            {visibleContingents.map(({ contingent, index }, visibleIndex) => (
              <TableRowContainer key={`contingent-${String(index)}`}>
                <TableShellTitle width={TITLE_WIDTH}>
                  {visibleContingents.length > 1
                    ? `Contingent ${String(visibleIndex + 1)}`
                    : "Contingent"}
                </TableShellTitle>
                <TableShellValue
                  padding={VALUE_SHELL_PADDING}
                  width={VALUE_WIDTH}
                >
                  <EditableTextContainer
                    item={{
                      text: contingentValueText(contingent),
                      labels: contingentValueLabels(contingent),
                    }}
                    refKey={createRefCode(
                      REF_CODE_FINANCIAL_TERMS_MA_CONTINGENT_VALUE,
                      index,
                    )}
                    canTextEdit
                    style={EDITABLE_TEXT_STYLE}
                  />
                </TableShellValue>
                <TableShellValue
                  padding={VALUE_SHELL_PADDING}
                  width={VALUE_PER_SHARE_WIDTH}
                >
                  <EditableTextContainer
                    item={{
                      text: contingentPricePerShareText(contingent),
                      labels: contingentPricePerShareLabels(contingent),
                    }}
                    refKey={createRefCode(
                      REF_CODE_FINANCIAL_TERMS_MA_CONTINGENT_PRICE_PER_SHARE,
                      index,
                    )}
                    canTextEdit
                    style={EDITABLE_TEXT_STYLE}
                  />
                </TableShellValue>
                <TableShellValue padding={VALUE_SHELL_PADDING}>
                  <EditableTextContainer
                    item={{
                      text: contingentMilestoneText(contingent),
                      labels: contingentMilestoneLabels(contingent),
                    }}
                    refKey={createRefCode(
                      REF_CODE_FINANCIAL_TERMS_MA_CONTINGENT_MILESTONE,
                      index,
                    )}
                    canTextEdit
                    style={EDITABLE_TEXT_STYLE}
                  />
                </TableShellValue>
              </TableRowContainer>
            ))}

            {showTotal && (
              <TableRowContainer>
                <TableShellTitle width={TITLE_WIDTH}>Total</TableShellTitle>
                <TableShellValue
                  padding={VALUE_SHELL_PADDING}
                  width={VALUE_WIDTH}
                >
                  <EditableTextContainer
                    item={{
                      text: totalValueText(terms),
                      labels: totalValueLabels(terms),
                    }}
                    refKey={REF_CODE_FINANCIAL_TERMS_MA_TOTAL_VALUE}
                    canTextEdit
                    style={EDITABLE_TEXT_STYLE}
                  />
                </TableShellValue>
                <TableShellValue
                  padding={VALUE_SHELL_PADDING}
                  width={VALUE_PER_SHARE_WIDTH}
                >
                  <EditableTextContainer
                    item={{
                      text: totalPricePerShareText(terms),
                      labels: totalPricePerShareLabels(terms),
                    }}
                    refKey={REF_CODE_FINANCIAL_TERMS_MA_TOTAL_PRICE_PER_SHARE}
                    canTextEdit
                    style={EDITABLE_TEXT_STYLE}
                  />
                </TableShellValue>
                <TableShellValue
                  padding={VALUE_SHELL_PADDING}
                  isEditable={false}
                >
                  {null}
                </TableShellValue>
              </TableRowContainer>
            )}
          </TableContainer>
        )}
        <TableContainer>
          {showNumShares && (
            <>
              <TableRowContainer>
                <TableShellTitle>Number of Acquiring Shares</TableShellTitle>
              </TableRowContainer>
              <TableRowContainer>
                <TableShellValue padding={VALUE_SHELL_PADDING}>
                  <EditableTextContainer
                    item={{
                      text: numSharesText(terms),
                      labels: numSharesLabels(terms),
                    }}
                    refKey={REF_CODE_FINANCIAL_TERMS_MA_NUM_SHARES}
                    canTextEdit
                    style={EDITABLE_TEXT_STYLE}
                  />
                </TableShellValue>
              </TableRowContainer>
            </>
          )}

          {showOwnership && (
            <>
              <TableRowContainer>
                <TableShellTitle>Ownership & Exchange Terms</TableShellTitle>
              </TableRowContainer>
              <TableRowContainer>
                <TableShellValue padding={VALUE_SHELL_PADDING}>
                  <EditableTextContainer
                    item={{
                      text: ownershipExchangeTermsText(terms),
                      labels: [],
                    }}
                    refKey={REF_CODE_FINANCIAL_TERMS_MA_OWNERSHIP_EXCHANGE}
                    canTextEdit
                    style={EDITABLE_TEXT_STYLE}
                  />
                </TableShellValue>
              </TableRowContainer>
            </>
          )}

          {showTermDetails && (
            <>
              <TableRowContainer>
                <TableShellTitle>Financial Term Details</TableShellTitle>
              </TableRowContainer>
              <TableRowContainer>
                <TableShellValue padding={VALUE_SHELL_PADDING}>
                  <EditableTextContainer
                    item={{
                      text: financialTermDetailsText(terms),
                      labels: [],
                    }}
                    refKey={REF_CODE_FINANCIAL_TERMS_MA_TERM_DETAILS}
                    canTextEdit
                    style={EDITABLE_TEXT_STYLE}
                  />
                </TableShellValue>
              </TableRowContainer>
            </>
          )}
        </TableContainer>
      </S.ParagraphFinancialTermsMAWrapper>
    </ParagraphSectionLayout>
  );
};

export default DealsParagraphFinancialTermsMA;
